import type { FieldValues } from "react-hook-form";
import type { FieldStateHelperAPI, HookFormFieldStateHelperParams } from "./fieldStateHelper.types";

export const hookFormFieldStateHelper = <FormType extends FieldValues>({
    errors,
    touched,
    isSubmitted,
}: HookFormFieldStateHelperParams<FormType>): FieldStateHelperAPI => {
    const isFieldInvalid = (fieldName: string) => {
        const error = getFieldError(fieldName);
        const isTouched = isFieldTouched(fieldName);
        return Boolean(error) && Boolean(isTouched);
    };

    const getFieldError = (fieldName: string) => {
        return errors?.[fieldName]?.message as string | undefined;
    };

    const isFieldTouched = (fieldName: string) => {
        // Need to cast touched because react-hook-form's touchedFields is a dynamic type which cannot be inferred like FieldValues.
        return Boolean((touched as Record<string, boolean>)?.[fieldName]) || Boolean(isSubmitted);
    };

    const isFieldSuccess = (fieldName: string) => {
        return Boolean(!errors?.[fieldName] && isFieldTouched(fieldName));
    };

    const getFieldStateProps = (fieldName: string) => {
        return {
            isInvalid: isFieldInvalid(fieldName),
            isSuccess: isFieldSuccess(fieldName),
            error: getFieldError(fieldName),
        };
    };

    const getIconProps: FieldStateHelperAPI["getIconProps"] = (fieldName) => {
        const isSuccess = isFieldSuccess(fieldName);
        if (isSuccess) {
            return {
                rightIcon: "ok",
            };
        }
        return {};
    };

    return {
        isFieldInvalid,
        getFieldError,
        isFieldTouched,
        isFieldSuccess,
        getFieldStateProps,
        getIconProps,
    };
};
